import {
  FunctionComponent,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import DropdownMenu from "../../components/dropdown/DropdownMenu";
import SvgFragment from "../SvgFragment";
import Row from "../../components/Row";
import Text from "../../components/Text";
import Checkbox from "../../components/Checkbox";
import { IConnection } from "../../interfaces/models/connection";
import { IFeed } from "../../interfaces/models/feed";
import { AppRoutes } from "../../interfaces/store/appStore";
import { useFeedsStore } from "../../stores/feeds/useFeedsStore";
import { useFeedsHandlers } from "../../services/actions/feeds/useFeedsHandlers";
import styled, { createGlobalStyle } from "styled-components";
import { styles } from "../../styles/themes/style";

interface IConnectionActionWithFeedsDropdownProps {
  connection: IConnection;
  onUpdate?: (
    updatedFeeds?: { _id: string; name: string; dateAdded?: string }[]
  ) => void;
  onCreateFeed?: (connectionId: string) => void;
  className?: string;
  currentFeedId?: string; // ID of the current feed being viewed
}

const ConnectionActionWithFeedsDropdown: FunctionComponent<
  IConnectionActionWithFeedsDropdownProps
> = ({ connection, onUpdate, onCreateFeed, className, currentFeedId }) => {
  const navigate = useNavigate();
  const {
    store: { feeds },
  } = useFeedsStore();
  const {
    bulkAddConnectionsToFeedHandler,
    bulkRemoveConnectionsFromFeedHandler,
  } = useFeedsHandlers();
  const [isUpdating, setIsUpdating] = useState<{ [key: string]: boolean }>({});
  const [showFeeds, setShowFeeds] = useState(false);
  const [feedsPosition, setFeedsPosition] = useState({ x: 0, y: 0 });
  const [forceCloseMainDropdown, setForceCloseMainDropdown] = useState(false);
  const [optimisticFeedStates, setOptimisticFeedStates] = useState<{
    [feedId: string]: boolean;
  }>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const connectionFeeds = useMemo(
    () => connection.feeds || [],
    [connection.feeds]
  );
  const connectionFeedIds = useMemo(
    () => connectionFeeds.map((feed) => feed._id),
    [connectionFeeds]
  );

  const handleFeedToggle = useCallback(
    async (feedId: string, isCurrentlyInFeed: boolean) => {
      setIsUpdating((prev) => ({ ...prev, [feedId]: true }));

      // Optimistic UI update - immediately toggle the checkbox
      setOptimisticFeedStates((prev) => ({
        ...prev,
        [feedId]: !isCurrentlyInFeed,
      }));

      try {
        let result;
        if (isCurrentlyInFeed) {
          // Remove from feed
          result = await bulkRemoveConnectionsFromFeedHandler(feedId, [
            connection._id,
          ]);
        } else {
          // Add to feed
          result = await bulkAddConnectionsToFeedHandler(
            [connection._id],
            [feedId]
          );
        }

        if (result?.success) {
          // Calculate the updated feeds
          let updatedFeeds = [...connectionFeeds];
          if (isCurrentlyInFeed) {
            // Remove feed
            updatedFeeds = updatedFeeds.filter((feed) => feed._id !== feedId);
          } else {
            // Add feed - find the feed info from the feeds store
            const feedToAdd = feeds.find((f: IFeed) => f._id === feedId);
            if (feedToAdd) {
              updatedFeeds.push({ _id: feedToAdd._id, name: feedToAdd.name });
            }
          }

          // If connection was removed from the current feed, close both dropdowns
          if (isCurrentlyInFeed && currentFeedId === feedId) {
            setShowFeeds(false);
            setForceCloseMainDropdown(true);

            // Reset forceClose after a short delay
            setTimeout(() => {
              setForceCloseMainDropdown(false);
            }, 100);
          }

          if (onUpdate) {
            onUpdate(updatedFeeds);
          }
        } else {
          // Revert optimistic update on failure
          setOptimisticFeedStates((prev) => {
            const newState = { ...prev };
            delete newState[feedId];
            return newState;
          });
        }
      } catch (error) {
        console.error("Error toggling feed:", error);

        // Revert optimistic update on error
        setOptimisticFeedStates((prev) => {
          const newState = { ...prev };
          delete newState[feedId];
          return newState;
        });
      } finally {
        setIsUpdating((prev) => ({ ...prev, [feedId]: false }));
      }
    },
    [
      connection._id,
      bulkAddConnectionsToFeedHandler,
      bulkRemoveConnectionsFromFeedHandler,
      onUpdate,
      currentFeedId,
      connectionFeeds,
      feeds,
    ]
  );

  const calculatePosition = useCallback(
    (element: HTMLElement | null) => {
      if (!element) return { x: 0, y: 0 };

      const rect = element.getBoundingClientRect();

      // Calculate smart positioning based on screen boundaries
      const dropdownWidth = 200; // minWidth of dropdown
      const gap = 8;

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Get actual dropdown height if available, or use estimate
      const dropdownHeight =
        dropdownRef.current?.offsetHeight ||
        Math.min(feeds.length * 32 + 100, 400); // estimate based on feeds count

      // Default position (right side)
      let x = rect.right + gap;
      let y = rect.top - 6;

      // Check if dropdown would overflow on the right side
      if (x + dropdownWidth > viewportWidth) {
        // Position on the left side
        x = rect.left - dropdownWidth - gap;
      }

      // Check if dropdown would overflow on the bottom
      if (y + dropdownHeight > viewportHeight) {
        // Position above the trigger element
        y = rect.bottom - dropdownHeight + 6;

        // If still overflowing at top, align to bottom of viewport
        if (y < 0) {
          y = viewportHeight - dropdownHeight - 20; // 20px margin from bottom
        }
      }

      // Ensure minimum margins from edges
      if (x < 10) x = 10;
      if (y < 10) y = 10;

      return { x, y };
    },
    [feeds]
  );

  const handleFeedsClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      const position = calculatePosition(e.currentTarget as HTMLElement | null);
      setFeedsPosition(position);
      setShowFeeds(!showFeeds);
    },
    [showFeeds, calculatePosition]
  );

  const handleFeedsHover = useCallback(
    (e: React.MouseEvent) => {
      // Clear any existing timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }

      // Save element reference before timeout
      const element = e.currentTarget as HTMLElement | null;

      // Set timeout to show feeds after a short delay
      hoverTimeoutRef.current = setTimeout(() => {
        const position = calculatePosition(element);
        setFeedsPosition(position);
        setShowFeeds(true);
      }, 300); // 300ms delay
    },
    [calculatePosition]
  );

  const handleFeedsMouseLeave = useCallback(() => {
    // Clear the timeout if user moves mouse away before delay
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    // Set timeout to hide feeds after a short delay (allows moving to dropdown)
    hoverTimeoutRef.current = setTimeout(() => {
      setShowFeeds(false);
    }, 200);
  }, []);

  const handleDropdownMouseEnter = useCallback(() => {
    // Clear timeout when mouse enters dropdown
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }
  }, []);

  const handleDropdownMouseLeave = useCallback(() => {
    // Hide dropdown when mouse leaves
    setShowFeeds(false);
  }, []);

  // Close feeds dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showFeeds) {
        setShowFeeds(false);
      }
    };

    if (showFeeds) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFeeds]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, []);

  // Close feeds dropdown when main dropdown is force closed
  useEffect(() => {
    if (forceCloseMainDropdown) {
      setShowFeeds(false);
    }
  }, [forceCloseMainDropdown]);

  // Clear optimistic state when connection.feeds is updated
  useEffect(() => {
    const newConnectionFeedIds = (connection.feeds || []).map(
      (feed) => feed._id
    );

    // Check if any optimistic states should be cleared
    setOptimisticFeedStates((prev) => {
      const newState = { ...prev };
      let hasChanges = false;

      Object.keys(prev).forEach((feedId) => {
        const optimisticValue = prev[feedId];
        const realValue = newConnectionFeedIds.includes(feedId);

        // If optimistic value matches real value, clear the optimistic state
        if (optimisticValue === realValue) {
          delete newState[feedId];
          hasChanges = true;
        }
      });

      return hasChanges ? newState : prev;
    });
  }, [connection.feeds]);

  // Recalculate position when dropdown becomes visible (for accurate height measurement)
  useEffect(() => {
    if (showFeeds && dropdownRef.current) {
      // Small delay to ensure dropdown is rendered
      setTimeout(() => {
        if (dropdownRef.current) {
          const currentRect = dropdownRef.current.getBoundingClientRect();
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;

          let { x, y } = feedsPosition;
          const needsRepositioning =
            x + currentRect.width > viewportWidth ||
            y + currentRect.height > viewportHeight;

          if (needsRepositioning) {
            // Recalculate with actual dimensions
            const gap = 8;

            // Find the trigger element position (approximate)
            const triggerX = x - gap;
            const triggerY = y + 6;
            const triggerWidth = 20; // approximate width

            let newX = triggerX + triggerWidth + gap; // right side
            let newY = triggerY - 6;

            // Check horizontal overflow
            if (newX + currentRect.width > viewportWidth) {
              newX = triggerX - currentRect.width - gap; // left side
            }

            // Check vertical overflow
            if (newY + currentRect.height > viewportHeight) {
              newY = triggerY - currentRect.height + 6; // above

              if (newY < 0) {
                newY = viewportHeight - currentRect.height - 20;
              }
            }

            // Ensure minimum margins
            if (newX < 10) newX = 10;
            if (newY < 10) newY = 10;

            setFeedsPosition({ x: newX, y: newY });
          }
        }
      }, 10);
    }
  }, [showFeeds, feedsPosition]);

  return (
    <>
    <FeedsActionDropdownGlobalStyles />
    <DropdownMenu
      className={`${className || ''} feeds-action-dropdown`.trim()}
      SVGtype="threeDots"
      $hideToggle
      $transparent
      tooltipMessage="Actions"
      forceClose={forceCloseMainDropdown}
    >
      <DropdownContainer>
        <FeedsDropdownItem
          onClick={handleFeedsClick}
          onMouseEnter={handleFeedsHover}
          onMouseLeave={handleFeedsMouseLeave}
        >
          <Row
            alignItems
            gap="5px"
            style={{ justifyContent: "space-between", width: "100%" }}
          >
            <Text>Feeds</Text>
            <SvgFragment type="arrowRight" />
          </Row>
        </FeedsDropdownItem>
      </DropdownContainer>

      {showFeeds &&
        createPortal(
          <FeedsDropdownPortal
            ref={dropdownRef}
            style={{
              position: "fixed",
              top: feedsPosition.y,
              left: feedsPosition.x,
              zIndex: 10000010,
            }}
            onMouseDown={(e) => e.stopPropagation()}
            onMouseEnter={handleDropdownMouseEnter}
            onMouseLeave={handleDropdownMouseLeave}
          >
            <div
              style={{
                minWidth: "200px",
                padding: "16px 0",
                background: "white",
                border: "1px solid #e0e0e0",
                borderRadius: "4px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              }}
            >
              <div style={{ padding: "0 12px 8px 12px" }}>
                <Text $paragraph2>Click to add/remove person</Text>
              </div>

              {feeds.map((feed: IFeed) => {
                const originalIsInFeed = connectionFeedIds.includes(feed._id);
                // Use optimistic state if available, otherwise use original state
                const isInFeed = optimisticFeedStates.hasOwnProperty(feed._id)
                  ? optimisticFeedStates[feed._id]
                  : originalIsInFeed;
                const isLoading = isUpdating[feed._id];

                return (
                  <FeedItem
                    key={feed._id}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isLoading) {
                        handleFeedToggle(feed._id, originalIsInFeed);
                      }
                    }}
                    style={{ opacity: isLoading ? 0.6 : 1 }}
                  >
                    <Row alignItems gap="8px">
                      <Checkbox
                        checked={isInFeed}
                        onClickHandler={(e) => {
                          e.stopPropagation();
                          if (!isLoading) {
                            handleFeedToggle(feed._id, originalIsInFeed);
                          }
                        }}
                        checkboxId={`feed-${feed._id}-${connection._id}`}
                        disabled={isLoading}
                        $height="24px"
                      />
                      <div style={{ flex: 1 }}>
                        <Text>{feed.name}</Text>
                      </div>
                      {isLoading && (
                        <div style={{ width: "16px", height: "16px" }}>
                          <SvgFragment type="spinner" />
                        </div>
                      )}
                    </Row>
                  </FeedItem>
                );
              })}

              <div style={{ paddingTop: "8px" }}>
                <FeedItem
                  onClick={() => {
                    setShowFeeds(false);
                    if (onCreateFeed) {
                      onCreateFeed(connection._id);
                    } else {
                      // Fallback to navigation if onCreateFeed is not provided
                      navigate(AppRoutes.CONNECTIONS, {
                        state: {
                          showCreateFeedModal: true,
                          selectedConnectionId: connection._id,
                        },
                      });
                    }
                  }}
                >
                  <AddNewFeedRow alignItems gap="8px">
                    <SvgFragment type="plus" />
                    <div style={{ color: styles.colors.primary600 }}>
                      <Text>Create new feed</Text>
                    </div>
                  </AddNewFeedRow>
                </FeedItem>
              </div>
            </div>
          </FeedsDropdownPortal>,
          document.body
        )}
    </DropdownMenu>
    </>
  );
};

const FeedsActionDropdownGlobalStyles = createGlobalStyle`
  .feeds-action-dropdown .actions-dropdown + div {
    z-index: 10000010 !important;
  }
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const FeedsDropdownPortal = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const FeedItem = styled.div`
  padding: 2px 12px;
  cursor: pointer;

  &:hover {
    background-color: #f5f5f5;
  }
`;

const FeedsDropdownItem = styled.div`
  padding: 8px 16px;
  cursor: pointer;
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  transition: background-color 0.2s ease;

  &:hover {
    svg path {
      fill: ${styles.colors.primary600};
    }

    span {
      color: ${styles.colors.primary600};
    }
  }
`;

const AddNewFeedRow = styled(Row)`
  & svg path {
    fill: ${styles.colors.primary600};
  }
`;

export default ConnectionActionWithFeedsDropdown;
